"use client";
import Link from "next/link";
import { ArrowRight, Code2 } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { statusApi as data } from "@/lib/data/status";

const accent = "var(--color-crimson)";

export function StatusApi() {
  return (
    <Section id="status-api" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-crimson)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Endpoint</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Method</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {data.endpoints.map((e) => (
                    <tr key={e.endpoint} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top font-mono text-xs" style={{ color: accent }}>{e.endpoint}</td>
                      <td className="px-4 py-3 align-top"><span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, color: accent }}>{e.method}</span></td>
                      <td className="px-4 py-3 align-top hidden md:table-cell text-[rgb(var(--fg-muted))]">{e.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-8 grid lg:grid-cols-2 gap-5">
          <Reveal delay={200}>
            <div className="glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <Code2 className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Response Format</span>
              </div>
              <pre className="mt-3 text-xs font-mono text-[rgb(var(--fg-muted))] whitespace-pre-wrap leading-relaxed">{data.responseFormat}</pre>
            </div>
          </Reveal>
          <Reveal delay={250}>
            <div className="glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Rate Limits & Authentication</span>
              <div className="mt-3 space-y-2">
                {data.rateLimits.map((r) => (
                  <div key={r.tier} className="text-sm">
                    <span className="font-semibold">{r.tier}:</span> <span className="text-[rgb(var(--fg-muted))]">{r.rpm} rpm · burst {r.burst}</span>
                  </div>
                ))}
                <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{data.auth.body}</p>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
